/**
 * studio/src/lib/user-identity.ts
 *
 * The shared identity surface for the logged-in user.
 *
 * Why this exists (2026-06-25 follow-up):
 *   The Studio has TWO places that show "who am I?":
 *     1. The desktop profile-menu dropdown (header, avatar + dropdown)
 *     2. The mobile drawer (when the hamburger is tapped)
 *   Before this composable, both surfaces computed the avatar
 *   fallback chain and the display label independently in their
 *   own component setup blocks. That's the same load-bearing logic
 *   in two places — a recipe for silent drift if the avatar chain
 *   grows a fourth fallback or the display-label heuristic changes.
 *
 *   This composable is the single source of truth. Both
 *   `ProfileMenu.vue` (desktop dropdown) and `App.vue` (mobile
 *   drawer identity block) consume it. Tests in
 *   `user-identity.test.ts` pin the avatar chain + display label
 *   in isolation; component tests verify the composable is wired
 *   into the markup correctly.
 *
 * Avatar fallback chain (in order, picked at render time):
 *   1. Real photo — `creators.avatarFileId` → `getAvatarUrl()` →
 *      Appwrite Storage preview URL on the `user-media` bucket.
 *   2. Appwrite Avatars — `getAccountInitialsUrl(seed)` → server-
 *      rendered PNG of the user's initials on a name-persisted
 *      background. The Appwrite `services.avatars` flag is on in
 *      appwrite.config.json; no new infra required.
 *   3. CSS letter — when the user has no name AND no email (e.g.
 *      mid-logout), a single letter on a gray block. This is the
 *      only path that does NOT render an `<img>`.
 *
 * Display label:
 *   `userName || userEmail || "Account"` — name preferred, then
 *   email as the user-readable fallback, then a generic label.
 *   Deliberately does NOT consult `creators.displayName`; the
 *   user-scoped identity surface stays distinct from the creator-
 *   scoped editor (the /account view surfaces the creator handle +
 *   display name on its own panel).
 */
import { computed } from "vue";
import { useAuthStore } from "./auth";
import { getAccountInitialsUrl, getAvatarUrl } from "./appwrite";

export interface UserIdentity {
  /**
   * The avatar image source, picked from the fallback chain.
   * `null` when the user has no name AND no email — callers
   * must render the `.avatar-letter` placeholder instead.
   */
  avatarImgSrc: import("vue").ComputedRef<string | null>;
  /**
   * Single-letter fallback for the no-name-no-email case. Always
   * defined; `"?"` when no seed is available.
   */
  avatarLetter: import("vue").ComputedRef<string>;
  /**
   * The user-facing display label, composed from the Appwrite
   * user's name (preferred) + email (fallback). `"Account"` is
   * the last-resort label for the no-data case.
   */
  displayLabel: import("vue").ComputedRef<string>;
  /**
   * The Appwrite user's email (for the sub-line under the
   * display label). `null` when the Appwrite user has no email
   * (rare; can happen during the magic-link callback race).
   */
  userEmail: import("vue").ComputedRef<string | null>;
  /**
   * The Appwrite user ID. `null` when not logged in.
   */
  userId: import("vue").ComputedRef<string | null>;
}

/**
 * Returns the shared identity surface for the logged-in user.
 *
 * The returned bundle is composed of `ComputedRef` instances —
 * Vue's reactivity tracks the underlying auth store refs, so
 * consumers re-render automatically when the user changes
 * (login, logout, name update). In `<script setup>`, the returned
 * refs are auto-unwrapped when referenced in the template (or
 * used with `ref(...)`-style destructuring).
 *
 * This composable is intentionally NOT a Pinia store — it's a
 * pure read-side projection over the auth store. The auth store
 * remains the single source of truth for who is logged in.
 */
export function useUserIdentity(): UserIdentity {
  const auth = useAuthStore();

  const creator = computed(() => auth.getCachedCreator());
  const avatarFileId = computed(() => {
    const row = creator.value as { avatarFileId?: string | null } | null;
    return row?.avatarFileId ?? null;
  });

  const avatarImgSrc = computed(() => {
    // 1. Real photo from the Storage preview.
    const fileUrl = getAvatarUrl(avatarFileId.value, 72);
    if (fileUrl) return fileUrl;
    // 2. Appwrite Avatars service URL (server-rendered PNG of initials).
    const seed = auth.userName || auth.userEmail || "";
    if (!seed) return null; // letter placeholder
    return getAccountInitialsUrl(seed, 72);
  });

  const avatarLetter = computed(() => {
    const seed = auth.userName || auth.userEmail;
    if (!seed) return "?";
    return seed.charAt(0).toUpperCase();
  });

  const displayLabel = computed(() => {
    return auth.userName || auth.userEmail || "Account";
  });

  return {
    avatarImgSrc,
    avatarLetter,
    displayLabel,
    userEmail: computed(() => auth.userEmail ?? null),
    userId: computed(() => auth.userId ?? null),
  };
}
